import * as React from "react";
import { ToggleButton, ToggleButtonProps } from "@bizform/primereact/components/togglebutton/ToggleButton";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Checkbox } from "@bizform/primereact/checkbox";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = ToggleButtonProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    onIcon?: string;
    offIcon?: string;
    onLabel?: string;
    offLabel?: string;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    checked?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};

    disabled?: boolean;

    // event
    onChange?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * Toggle Button component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<boolean>>()
export class BfToggleButton extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_u4siAgQf1fqhyKcxrHfy5cED_hZOU6f",
        category: ComponentCategory.BUTTON,
        label: I18n.componentElement.togglebutton.label,
        description: I18n.componentElement.togglebutton.description,
        icon: "",
        dataType: [DataType.BOOLEAN],
        property: null
    };

    focus(): void {
        if (this._toggleButton && this._toggleButton.hasOwnProperty("input")) {
            const targetObject = (this._toggleButton as any);
            if (targetObject.input && typeof targetObject.input.focus === "function") {
                targetObject.input.focus();
            }
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: boolean): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: boolean): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: false
                }
            };
            this.setState(newState);
        } else if ( typeof value === "boolean") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value
                }
            };
            this.setState(newState);
        } else if (typeof value === "number" && ( value === 0 || value === 1)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value ? true : false
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfToggleButton)
    static init(): ComponentElement<boolean> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _toggleButton: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                checked: false
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._toggleButton = null;
    }

    componentDidUpdate() {
        if (this._toggleButton && this.state.wrappedCompState.tabindex !== this.___tabindex) {
            ComponentHelper.tabindex(this.displayMode, this._toggleButton.querySelector("input"), this.state.wrappedCompState.tabindex);
            this.___tabindex = this.state.wrappedCompState.tabindex;
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            // change state and then emit
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: this.accessibility ? e.checked : e.value
                }
            };
            // setState is an async function
            this.setState(newState, this._callbackSetState);
        }
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.checked ? true : false);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            /*
             * ToggleButton doesn't have readOnly property.
             */
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: any): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <Checkbox
                    {...newProps}
                    inputId={this.elementId}
                    key={key}
                    ref={this._setRef}
                />
            );
        } else {
            return (
                <ToggleButton
                    {...newProps}
                    onIcon={newProps.onIcon ? newProps.onIcon : " "}
                    offIcon={newProps.offIcon ? newProps.offIcon : " "}
                    key={key}
                    id={this.elementId}
                    ref={this._setRef}
                    style={{ ...this.state.wrappedCompState.style, width: "100%" }}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{`${this.state.wrappedCompState.checked}`}</div>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._toggleButton = this.accessibility ? ref.element : ref.container;
            this.readyComponent();
        }
    }
}
